/** @prettier */

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';

//MTH component import
import NoMoreRegister from '../../component/NoMoreRegister';
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
import APIErrorMessage from '../../component/APIErrorMessage';
//API import
import {
  postCompositionsGroups,
  getCompositionsGroups,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {sortByUnicode} from '../../functions/sort';
import {format, limit} from '../../define';
import {useTranslation} from 'react-i18next';
import {
  AddButton,
  PrimaryButton,
  SecondaryButton,
} from '../../component/BasicButton';

/**
 * グループ追加ダイアログ
 * @param props props
 * @returns JSX
 */
const GroupAddDialog = (props: {
  addButtonName: string;
  unitSystem: 'SL' | 'RT' | 'Modbus';
  setGroupInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getCompositionsGroups | null>
  >;
  groupInfoData: apiIF.res.getCompositionsGroups | null;
}) => {
  const trans = useTranslation().t; //翻訳関数
  //パスパラメータ取得
  const {siteID, floorID} = useParams();

  const [groupName, setGroupName] = useState(''); //グループの名前
  const [isOpenDialog, setIsOpenDialog] = useState(false); //ダイアログ表示フラグ（追加ダイアログ・登録超過ダイアログ）
  const [okBtnDisabled, setOKBtnDisabled] = useState(true); //OKボタン非表示フラグ
  const [groupNumber, setGroupNumber] = useState(0); //グループの登録数
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  //TextBoxのエラー状態
  const [groupNameError, setGroupNameError] = useState(false); //グループの名前

  //入力項目のチェック
  useEffect(() => {
    if (groupName === '' || groupNameError || apiWait) {
      setOKBtnDisabled(true);
    } else {
      setOKBtnDisabled(false);
    }
  }, [groupName, groupNameError, apiWait]);

  //登録台数を算出
  useEffect(() => {
    if (props.groupInfoData === null) {
      return;
    }

    let slNum = 0;
    let rtNum = 0;
    let modbusNum = 0;
    for (const groupInfo of props.groupInfoData['group-informations']) {
      if (groupInfo['unit-system'] === 'SL') {
        slNum++;
      } else if (groupInfo['unit-system'] === 'RT') {
        rtNum++;
      } else if (groupInfo['unit-system'] === 'Modbus') {
        modbusNum++;
      }
    }
    setGroupNumber(slNum + rtNum + modbusNum);
  }, [props.groupInfoData]);

  /**
   * ADD GROUPボタン押下時処理
   */
  const handleClickOpen = () => {
    setIsOpenDialog(true);
  };

  /**
   * OKボタン押下時処理
   */
  const addGroup = () => {
    //パスパラメータを取得できない場合
    if (siteID === undefined) {
      return;
    }
    if (props.unitSystem === 'SL' && floorID === undefined) {
      return;
    }

    setOKBtnDisabled(true);
    setAPIWait(true);

    //グループを追加
    const addData: apiIF.reqParameters.postCompositionsGroups = {
      'group-name': groupName,
      'floor': floorID === undefined ? '0' : floorID, //hyozan、modbusのグループは0になる
      'unit-system': props.unitSystem,
    };
    postCompositionsGroups(siteID, addData)
      .then(() => {
        //グループの情報を取得
        return getCompositionsGroups(siteID, {'max-result': 400});
      })
      .then(response => {
        //表示内容をUnicode順に並び替え
        response['group-informations'].sort(sortByUnicode('group-name'));
        for (const info of response['group-informations']) {
          if (info['unit-informations'] === undefined) continue;
          info['unit-informations'].sort(sortByUnicode('unit-name'));
        }
        props.setGroupInfo(() => response);
        handleClose();
      })
      .catch(() => {
        setOKBtnDisabled(false);
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        setAPIWait(false);
      });
  };
  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    setGroupName('');
    setOKBtnDisabled(true);
    setIsOpenDialog(false);
  };

  return (
    <>
      {/* グループ追加ボタン */}
      <AddButton
        onClick={handleClickOpen}
        id={'mv-btnAddGp'}
        buttonText={props.addButtonName}
      />
      {/* 登録台数が上限に達している場合は、追加付加のメッセージを表示 */}
      {groupNumber >= limit.REGISTRATION_NUM_GROUP_MAX ? (
        <NoMoreRegister
          open={isOpenDialog}
          setOpen={setIsOpenDialog}
          title={trans('NoMoreRegist.errorDlg.title')}
          text={trans('NoMoreRegist.errorDlg.msg')}
        />
      ) : (
        //ダイアログ
        <Dialog
          open={isOpenDialog}
          onClose={handleClose}
          aria-labelledby="form-dialog-title">
          <DialogTitle>{trans('GroupAddDialog.dialogBox.title')}</DialogTitle>
          <DialogContent>
            {/* ダイアログに表示するメッセージ */}
            <DialogContentText>
              {trans('GroupAddDialog.dialogBox.msg')}
            </DialogContentText>
            {/* 追加するグループ名称 */}
            <ChkInputTextbox
              setValue={setGroupName}
              setError={setGroupNameError}
              chkLength={format.BASIC_LENGTH_MAX}
              type="text"
              label={trans('GroupAddDialog.dialogBox.txtField.label.groupName')}
              value={groupName}
              id="mv-inputGpName"
              class="groupSetting__contents__groupNameTextBox"
              helperText=" "
              errHelperText={trans(
                'GroupAddDialog.dialogBox.txtField.errHelperTxt'
              )}
              variant="standard"
              autoFocus={true}
              margin="dense"
              onKeyDown={event => {
                if (event.key === 'Enter' && okBtnDisabled === false) {
                  event.preventDefault();
                  addGroup();
                }
              }}
            />
          </DialogContent>
          <DialogActions>
            <SecondaryButton
              disabled={apiWait}
              onClick={handleClose}
              buttonText={trans('GroupAddDialog.dialogBox.buttonTxt.cancel')}
              id={'mv-groupAddCancelBtn'}
            />
            <PrimaryButton
              onClick={addGroup}
              buttonText={trans('GroupAddDialog.dialogBox.buttonTxt.ok')}
              id="mv-btnOkAddGp"
              disabled={okBtnDisabled}
              className="BtnOk"
              apiWait={apiWait}
            />
          </DialogActions>
        </Dialog>
      )}
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
        id={'mv-groupAddErrDlgBtn'}
      />
    </>
  );
};
export default GroupAddDialog;
